import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../lib/api';
import { getCachedOrFetch, invalidateCache } from '../lib/apiCache';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const persistAuthenticatedUser = (userData) => {
  localStorage.setItem('user', JSON.stringify(userData));

  if (userData?.organization_id) {
    localStorage.setItem('activeOrganizationId', userData.organization_id);
  } else {
    localStorage.removeItem('activeOrganizationId');
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser && storedUser !== "undefined") {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        persistAuthenticatedUser(parsedUser);

        // Verify token is still valid using client cache helper
        getCachedOrFetch('user_me', () => authApi.getMe(), 60000)
          .then((response) => {
            setUser(response.data);
            persistAuthenticatedUser(response.data);
          })
          .catch(() => {
            logout();
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (e) {
        console.error("Failed to parse stored user:", e);
        logout();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, workspaceId = null, twoFactorData = null) => {
    let response;
    
    if (twoFactorData) {
      // 2FA Verification step
      response = await authApi.verifyTwoFactor(twoFactorData.token, twoFactorData.code);
    } else {
      // Initial login step
      response = await authApi.login(email, password, workspaceId);
    }
    
    if (response.data.status === '2fa_required') {
      return response.data;
    }
    
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    persistAuthenticatedUser(userData);
    setUser(userData);
    
    return userData;
  };

  const register = async (data) => {
    const response = await authApi.register(data);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    persistAuthenticatedUser(userData);
    setUser(userData);
    
    return userData;
  };

  const setSession = (access_token, userData) => {
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeOrganizationId');
    invalidateCache();
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    setSession,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
